import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSubscription } from '../lib/useSubscription'
import { openCheckout, paddleConfig } from '../lib/paddle'
import { supabase } from '../lib/supabase'

const teal = '#0D9488'
const plum = '#1E1040'
const cream = '#FAF7F2'

/**
 * Banner shown above the app shell in two cases:
 *   1. Trial is active but <= 3 days remaining (warn before paywall)
 *   2. Trial expired / past period_end (read-only mode active)
 *
 * Returns null in all other cases (active sub, lifetime, etc.)
 */
export default function TrialBanner() {
  const { t } = useTranslation()
  const sub = useSubscription()
  const [opening, setOpening] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  // Don't show if loading, lifetime, paid active, canceled-but-still-active, or dismissed
  if (sub.loading) return null
  if (sub.lifetime) return null
  if (sub.paidActive && sub.status === 'active') return null
  if (sub.status === 'canceled' && sub.currentPeriodEnd && sub.currentPeriodEnd > new Date()) return null

  const isExpired = !sub.canWrite
  const isTrialEndingSoon = sub.trialActive && sub.daysRemaining !== null && sub.daysRemaining <= 3

  // Don't show if user dismissed and trial isn't urgent
  if (dismissed && !isExpired) return null

  // Don't show if no relevant state
  if (!isExpired && !isTrialEndingSoon) return null

  const handleUpgrade = async () => {
    setOpening(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return
      await openCheckout({
        userEmail: user.email,
        userId: user.id,
        onSuccess: () => setTimeout(() => sub.reload(), 2500),
        onClose: () => setOpening(false),
      })
    } catch {
      setOpening(false)
    }
  }

  // Style: red urgent for expired, teal-ish for trial reminder
  const isUrgent = isExpired
  const bgGradient = isUrgent
    ? 'linear-gradient(90deg, #DC2626, #B91C1C)'
    : `linear-gradient(90deg, ${plum}, ${teal})`

  let message
  if (isExpired) {
    message = t('trial.expired')
  } else if (sub.daysRemaining === 0) {
    message = t('trial.ends_today')
  } else if (sub.daysRemaining === 1) {
    message = t('trial.ends_tomorrow')
  } else {
    message = t('trial.days_left', { count: sub.daysRemaining })
  }

  return (
    <div style={{ ...styles.banner, background: bgGradient }}>
      <div style={styles.content}>
        <span style={styles.message}>{message}</span>
        <div style={styles.actions}>
          <button
            style={styles.cta}
            onClick={handleUpgrade}
            disabled={opening}
          >
            {opening ? t('trial.opening') : t('trial.upgrade_cta', { price: paddleConfig.priceLabel })}
          </button>
          {!isUrgent && (
            <button
              style={styles.dismissBtn}
              onClick={() => setDismissed(true)}
              aria-label={t('trial.dismiss')}
            >
              ✕
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const styles = {
  banner: {
    width: '100%',
    color: cream,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    position: 'sticky',
    top: 0,
    zIndex: 200,
  },
  content: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '12px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '14px',
    flexWrap: 'wrap',
  },
  message: {
    fontSize: '14px',
    fontWeight: 600,
    flex: '1 1 auto',
    minWidth: '180px',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  cta: {
    padding: '8px 16px',
    background: cream,
    color: plum,
    border: 'none',
    borderRadius: '100px',
    fontSize: '13px',
    fontWeight: 800,
    cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    whiteSpace: 'nowrap',
  },
  dismissBtn: {
    background: 'rgba(255,255,255,0.2)',
    color: cream,
    border: 'none',
    borderRadius: '50%',
    width: '28px',
    height: '28px',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
  },
}
